import { useCallback, useEffect, useRef } from 'react';
import { WBBox } from '@admiin-com/ds-web';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { CodeNode } from '@lexical/code';
import { TableNode, TableRowNode, TableCellNode } from '@lexical/table';
import { LinkNode } from '@lexical/link';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { $generateHtmlFromNodes } from '@lexical/html';
import { ToolbarPlugin } from './components/ToolbarPlugin';
import { DocxDropPlugin } from './components/DocxDropPlugin';
import { LayoutPlugin } from './plugins/LayoutPlugin';
import { LayoutContainerNode } from './nodes/LayoutContainerNode';
import { LayoutItemNode } from './nodes/LayoutItemNode';
import { restoreHandlebarsBlockPlaceholders } from '../../../helpers/handlebarsPlaceholders';
import {
  StyledEditorContainer,
  StyledContentEditable,
  StyledPlaceholder,
} from './DocumentEditor.styles';
import { EDITOR_CONFIG } from './DocumentEditor.constants';
import {
  DocumentEditorProvider,
  useDocumentEditorContext,
} from './context/DocumentEditorContext';
import { TemplateType } from '@admiin-com/ds-graphql';

interface DocumentEditorProps {
  onChange?: (value: string, html: string, includeLetterhead: boolean) => void;
  placeholder?: string;
  initialContent?: string;
  includeLetterhead?: boolean;
  onLetterheadChange?: (includeLetterhead: boolean) => void;
  templateType?: TemplateType;
}

// Helper component to initialize editor refs when the editor is ready
const EditorInitializer: React.FC<{
  latestEditorStateRef: React.MutableRefObject<any>;
  latestEditorRef: React.MutableRefObject<any>;
}> = ({ latestEditorStateRef, latestEditorRef }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Initialize refs with current editor state when component mounts
    const initializeRefs = () => {
      const editorState = editor.getEditorState();
      latestEditorStateRef.current = editorState;
      latestEditorRef.current = editor;
    };

    // Set initial values
    initializeRefs();

    // Also listen for any updates to keep refs current
    return editor.registerUpdateListener(({ editorState }) => {
      latestEditorStateRef.current = editorState;
      latestEditorRef.current = editor;
    });
  }, [editor, latestEditorStateRef, latestEditorRef]);

  return null; // This component doesn't render anything
};

/**
 * Ensures a valid Lexical editor state with at least one paragraph node.
 * Returns the original content if valid, or a minimal valid state if empty.
 */
const ensureValidEditorState = (
  content: string | undefined
): string | undefined => {
  if (!content) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(content);

    // Check if root exists and has empty children array
    if (
      parsed?.root &&
      Array.isArray(parsed.root.children) &&
      parsed.root.children.length === 0
    ) {
      // Return a minimal valid state with one empty paragraph
      return JSON.stringify({
        root: {
          children: [
            {
              children: [],
              direction: null,
              format: '',
              indent: 0,
              type: 'paragraph',
              version: 1,
              textFormat: 0,
              textStyle: '',
            },
          ],
          direction: 'ltr',
          format: '',
          indent: 0,
          type: 'root',
          version: 1,
        },
      });
    }

    return content;
  } catch {
    // If parsing fails, return undefined to let Lexical use its default
    return undefined;
  }
};

const DocumentEditorContent: React.FC<
  Omit<
    DocumentEditorProps,
    'includeLetterhead' | 'onLetterheadChange' | 'templateType'
  >
> = ({ onChange, placeholder = 'Start typing...', initialContent }) => {
  const { includeLetterhead } = useDocumentEditorContext();
  const latestEditorStateRef = useRef<any>(null);
  const latestEditorRef = useRef<any>(null);
  const initialConfig = {
    ...EDITOR_CONFIG,
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      CodeNode,
      TableNode,
      TableRowNode,
      TableCellNode,
      LinkNode,
      HorizontalRuleNode,
      LayoutContainerNode,
      LayoutItemNode,
    ],
    editorState: ensureValidEditorState(initialContent),
  };

  const generateCurrentHTML = useCallback(
    (editorState: any, editor: any) => {
      let html = restoreHandlebarsBlockPlaceholders(
        $generateHtmlFromNodes(editor, null)
      );

      // Wrap handlebars loop syntax with class for CSS targeting
      // Need to escape parentheses and handle both sorted and unsorted versions
      html = html.replace(
        /{{#each \(sortTasksByEntityType tasks\)}}/g,
        '<span class="hbs-loop-hidden">{{#each (sortTasksByEntityType tasks)}}</span>'
      );
      html = html.replace(
        /{{#each tasks}}/g,
        '<span class="hbs-loop-hidden">{{#each tasks}}</span>'
      );
      html = html.replace(
        /{{\/each}}/g,
        '<span class="hbs-loop-hidden">{{/each}}</span>'
      );

      // Add table styles and wrap HTML with padding for document preview
      const paddedHTML = `
        <div style="padding: 0px 40px 0px 40px;">
          <style>
            table {
              border-spacing: 0px;
              border-collapse: collapse;
              width: 100%;
            }

            th {
              background-color: transparent !important;
              font-weight: 400;
              padding: 8px;
            }

            th p {
              margin: 0;
            }

            td {
              padding: 8px;
            }

            td p {
              margin: 0
            }

            /* Hide handlebars loop syntax and parent table cells/rows */
            .hbs-loop-hidden {
              display: none !important;
              visibility: hidden !important;
            }

            td:has(.hbs-loop-hidden),
            tr:has(.hbs-loop-hidden) {
              display: none !important;
              visibility: hidden !important;
            }
          </style>
          ${html}
        </div>
      `.trim();

      return paddedHTML;
    },
    [includeLetterhead]
  );

  const handleEditorChange = useCallback(
    (editorState: any, editor: any) => {
      latestEditorStateRef.current = editorState;
      latestEditorRef.current = editor;

      if (onChange) {
        editorState.read(() => {
          const html = generateCurrentHTML(editorState, editor);
          onChange(
            JSON.stringify(editorState.toJSON()),
            html,
            includeLetterhead
          );
        });
      }
    },
    [onChange, generateCurrentHTML]
  );

  // Trigger onChange when letterhead state changes, even if editor content hasn't changed
  useEffect(() => {
    // Only trigger if we have both refs populated OR if we can get the current state
    if (onChange) {
      if (latestEditorStateRef.current && latestEditorRef.current) {
        // Use cached refs if available
        latestEditorStateRef.current.read(() => {
          const html = generateCurrentHTML(
            latestEditorStateRef.current,
            latestEditorRef.current
          );
          onChange(
            JSON.stringify(latestEditorStateRef.current.toJSON()),
            html,
            includeLetterhead
          );
        });
      }
      // Note: If refs are not available, the next content change will trigger onChange with letterhead
    }
  }, [includeLetterhead, onChange, generateCurrentHTML]);

  return (
    <StyledEditorContainer>
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <WBBox sx={{ position: 'relative' }}>
          <RichTextPlugin
            contentEditable={<StyledContentEditable />}
            placeholder={<StyledPlaceholder>{placeholder}</StyledPlaceholder>}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <TablePlugin />
          <LinkPlugin />
          <MarkdownShortcutPlugin />
          <DocxDropPlugin />
          <LayoutPlugin />
          <OnChangePlugin
            onChange={(editorState, editor) =>
              handleEditorChange(editorState, editor)
            }
          />
          <EditorInitializer
            latestEditorStateRef={latestEditorStateRef}
            latestEditorRef={latestEditorRef}
          />
        </WBBox>
      </LexicalComposer>
    </StyledEditorContainer>
  );
};

export const DocumentEditor: React.FC<DocumentEditorProps> = ({
  includeLetterhead,
  onLetterheadChange,
  templateType,
  ...props
}) => {
  return (
    <DocumentEditorProvider
      initialIncludeLetterhead={includeLetterhead}
      onLetterheadChange={onLetterheadChange}
      templateType={templateType}
    >
      <DocumentEditorContent {...props} />
    </DocumentEditorProvider>
  );
};
